import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";

/* M1 · Verify existing ABHA — Aadhaar OTP / Mobile OTP / ABHA number.
 * Step 1 → identifier + method → send OTP
 * Step 2 → verify OTP → receive profile + T-Token
 * Step 3 → optionally fetch card & link to patient.
 */
export default function VerifyABHAWizard({ patients = [], onLinked }) {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [method, setMethod] = useState("aadhaar"); // aadhaar | abha-number | mobile
  const [identifier, setIdentifier] = useState("");
  const [otp, setOtp] = useState("");
  const [txnId, setTxnId] = useState("");
  const [profile, setProfile] = useState(null);
  const [xToken, setXToken] = useState("");
  const [cardB64, setCardB64] = useState("");
  const [linkPatientId, setLinkPatientId] = useState("");

  const errText = (r) => r?.response?.error?.message || r?.response?.message || r?.error || r?.message || `HTTP ${r?.http_status || 0}`;

  const reset = () => { setStep(1); setBusy(false); setIdentifier(""); setOtp(""); setTxnId(""); setProfile(null); setXToken(""); setCardB64(""); setLinkPatientId(""); };

  const sendOTP = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/abdm/abha/verify/request-otp", { identifier, method });
      if (!data.ok) return toast.error(errText(data));
      setTxnId(data.response?.txnId || "");
      toast.success("OTP sent");
      setStep(2);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const verifyOTP = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/abdm/abha/verify/verify-otp", { txn_id: txnId, otp, method });
      if (!data.ok) return toast.error(errText(data));
      const r = data.response || {};
      setXToken(r.token || r.tokens?.token || "");
      // Some responses return profile inline; others require /profile/account
      const inlineProf = r.user || r.profile || r.ABHAProfile || null;
      if (inlineProf) {
        setProfile(inlineProf);
        setStep(3);
      } else if (r.token) {
        // fetch profile
        const p = await api.post("/abdm/abha/profile", { x_token: r.token || r.tokens?.token });
        if (p.data.ok) {
          setProfile(p.data.response || {});
        }
        setStep(3);
      } else {
        toast.warning("Verified but no profile returned");
        setStep(3);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const fetchCard = async () => {
    if (!xToken) return toast.error("No T-Token available");
    setBusy(true);
    try {
      const { data } = await api.post("/abdm/abha/card", { x_token: xToken });
      if (!data.ok) return toast.error(errText(data));
      setCardB64(data.response?.base64 || "");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const linkToPatient = async () => {
    if (!linkPatientId) return toast.error("Choose a patient");
    setBusy(true);
    try {
      const { data } = await api.post("/abdm/abha/link-to-patient", { patient_id: linkPatientId, abha_profile: profile, x_token: xToken });
      if (!data.ok) return toast.error("Link failed");
      toast.success("ABHA linked to patient");
      onLinked && onLinked();
      reset();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const idPlaceholder = method === "aadhaar" ? "12-digit Aadhaar"
    : method === "mobile" ? "10-digit mobile"
    : "14-digit ABHA number (91-XXXX-XXXX-XXXX)";

  return (
    <div className="bg-white border p-4 space-y-3" data-testid="abdm-verify-abha">
      <div className="flex items-center justify-between">
        <div className="label-tiny">M1 · Verify existing ABHA</div>
        <div className="flex gap-1">
          {[1, 2, 3].map((n) => (
            <div key={n} className={`w-6 h-6 text-[11px] flex items-center justify-center border rounded-full ${step >= n ? "bg-blue-700 text-white border-blue-700" : "text-slate-500"}`}>{n}</div>
          ))}
        </div>
      </div>

      <p className="text-[11px] text-blue-800 bg-blue-50 border border-blue-200 p-2 rounded-sm">
        Uses <code>/profile/login/request/otp</code> and <code>/profile/login/verify</code>. Identifier and OTP are RSA-encrypted per NHA v3 spec.
      </p>

      {step === 1 && (
        <div className="space-y-3">
          <div>
            <Label className="label-tiny">Method</Label>
            <div className="flex gap-1.5">
              {[["aadhaar", "Aadhaar OTP"], ["mobile", "ABHA-linked Mobile OTP"], ["abha-number", "ABHA Number OTP"]].map(([v, l]) => (
                <button key={v} type="button" data-testid={`vabha-m-${v}`}
                  onClick={() => setMethod(v)}
                  className={`px-2.5 py-1 text-[11px] border rounded-sm ${method === v ? "border-blue-700 bg-blue-50 text-blue-700 font-semibold" : "border-slate-300"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="label-tiny">{method === "aadhaar" ? "Aadhaar Number" : method === "mobile" ? "ABHA-linked Mobile" : "ABHA Number"}</Label>
            <Input data-testid="vabha-identifier" value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder={idPlaceholder}/>
          </div>
          <Button data-testid="vabha-send-otp" onClick={sendOTP} disabled={busy || !identifier} className="bg-blue-700 hover:bg-blue-800 rounded-sm">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : null} Send OTP
          </Button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-3">
          <div className="text-[11px] text-teal-800">OTP dispatched · txn <span className="font-mono">{txnId.slice(0, 12)}…</span></div>
          <div><Label className="label-tiny">Enter OTP</Label>
            <Input data-testid="vabha-otp" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} maxLength={6}/>
          </div>
          <div className="flex gap-2">
            <Button data-testid="vabha-verify" onClick={verifyOTP} disabled={busy || otp.length !== 6} className="bg-blue-700 hover:bg-blue-800 rounded-sm">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : null} Verify OTP
            </Button>
            <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-3">
          <div className="border-t pt-3 flex items-center gap-2 text-teal-800"><CheckCircle2 className="w-5 h-5"/> ABHA verified</div>
          {profile && (
            <div className="grid grid-cols-2 gap-2 text-xs bg-slate-50 border p-3">
              <div><span className="text-slate-500">ABHA Number:</span> <span className="font-mono">{profile.ABHANumber || profile.abhaNumber || "-"}</span></div>
              <div><span className="text-slate-500">ABHA Address:</span> <span className="font-mono">{profile.preferredAbhaAddress || profile.phrAddress || profile.abhaAddress || "-"}</span></div>
              <div><span className="text-slate-500">Name:</span> {[profile.firstName, profile.middleName, profile.lastName].filter(Boolean).join(" ") || profile.name || "-"}</div>
              <div><span className="text-slate-500">Gender:</span> {profile.gender || "-"}</div>
              <div><span className="text-slate-500">Mobile:</span> {profile.mobile || "-"}</div>
              <div><span className="text-slate-500">Email:</span> {profile.email || "-"}</div>
            </div>
          )}
          {cardB64 && <img alt="ABHA card" src={`data:image/png;base64,${cardB64}`} className="max-h-48 border" data-testid="vabha-card-img"/>}
          {!cardB64 && xToken && (
            <Button variant="outline" onClick={fetchCard} disabled={busy} data-testid="vabha-fetch-card">Fetch ABHA Card</Button>
          )}
          <div className="border-t pt-3">
            <Label className="label-tiny">Link to patient (tag Patient ID ↔ ABHA)</Label>
            <select value={linkPatientId} onChange={(e) => setLinkPatientId(e.target.value)} className="w-full h-9 border px-2 text-sm" data-testid="vabha-link-select">
              <option value="">-- Select patient --</option>
              {patients.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.uhid}</option>)}
            </select>
            <div className="flex gap-2 mt-2">
              <Button data-testid="vabha-link-btn" onClick={linkToPatient} disabled={busy || !linkPatientId} className="bg-teal-700 hover:bg-teal-800 rounded-sm">Link to Patient</Button>
              <Button variant="outline" onClick={reset}>New</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
